import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';
import { LIVE_QUEST_REWARD_SLOTS } from '../data/liveQuestRewards';
import { buildLiveQuestStickerPackPayload, StickerPackMember } from '../data/liveQuestStickerPack';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

const MAX_FILES = 100;

/** A Live Quest sticker pack holds 5 to 10 stickers. */
const LIVE_QUEST_PACK_MIN = 5;
const LIVE_QUEST_PACK_MAX = 10;
const LIVE_QUEST_PACK_REWARDS = LIVE_QUEST_REWARD_SLOTS.filter((reward) => reward.rewardType === 'stickerPack');

type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

type WalletShopMediaAsset = {
  Type: string;
  Url: string;
  MimeType?: string;
};

type ExistingSticker = {
  ProductKey: string;
  Environment?: 'dev' | 'prod';
  MediaAssets?: WalletShopMediaAsset[];
};

type StickerFile = {
  id: string;
  file: File;
  productKey: string;
  name: string;
  previewUrl: string;
  status: UploadStatus;
  error?: string;
  assetUrl?: string;
  savedProductKey?: string;
};

type PackSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const fileNameToKey = (filename: string): string =>
  filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toUpperCase();

const fileNameToLabel = (filename: string): string => {
  const base = filename.replace(/\.[^/.]+$/, '');
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const BulkStickerUpload: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReward = searchParams.get('liveQuest') || '';

  // Live Quest packs are rewards: free, never listed in the Shop.
  const [liveQuestOnly, setLiveQuestOnly] = useState(!!requestedReward);
  const [liveQuestRewardId, setLiveQuestRewardId] = useState(requestedReward);

  const [sharedSettings, setSharedSettings] = useState({
    PriceTokens: '10',
    DisplayOrderStart: '1',
    AvailabilityType: 'unlimited' as 'unlimited' | 'limited',
    Environment: 'prod' as 'dev' | 'prod',
  });

  const [stickerFiles, setStickerFiles] = useState<StickerFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [existingStickers, setExistingStickers] = useState<Map<string, ExistingSticker>>(new Map());
  const [packSaveStatus, setPackSaveStatus] = useState<PackSaveStatus>('idle');
  const [packSaveError, setPackSaveError] = useState('');

  // Fetch existing stickers once: used to auto-fill the next DisplayOrder, and to detect
  // when a queued file's derived ProductKey already exists so we replace its thumbnail
  // in place instead of creating a duplicate product.
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) return;
    fetch(`${walletApiBase}/getWalletShopProductsV2`, {
      method: 'POST',
      headers: getApiHeaders(token),
      body: JSON.stringify({
        UserID: 'dashboard',
        CategoryKey: 'STICKER',
        IncludeInactive: true,
      }),
    })
      .then((r) => r.json())
      .then((json) => {
        const products: (ExistingSticker & { DisplayOrder?: number })[] = json?.data?.products || [];
        const maxOrder = products.reduce((max, p) => Math.max(max, p.DisplayOrder ?? 0), 0);
        setSharedSettings((prev) => ({ ...prev, DisplayOrderStart: String(maxOrder + 1) }));
        setExistingStickers(new Map(products.map((p) => [p.ProductKey, p])));
      })
      .catch(() => {});
  }, []);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      stickerFiles.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    if (!selected.length) return;

    const remaining = MAX_FILES - stickerFiles.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} stickers allowed.`);
      return;
    }

    const capped = selected.slice(0, remaining);
    if (capped.length < selected.length) {
      toast.warn(`Only ${capped.length} of ${selected.length} files added (${MAX_FILES} max).`);
    }

    const newEntries: StickerFile[] = capped.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      file,
      productKey: fileNameToKey(file.name),
      name: fileNameToLabel(file.name),
      previewUrl: URL.createObjectURL(file),
      status: 'pending',
    }));

    setStickerFiles((prev) => [...prev, ...newEntries]);
    setPackSaveStatus('idle');
    setPackSaveError('');
    e.target.value = '';
  };

  const removeSticker = (id: string) => {
    setStickerFiles((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((s) => s.id !== id);
    });
    setPackSaveStatus('idle');
    setPackSaveError('');
  };

  const updateField = (id: string, field: 'productKey' | 'name', value: string) => {
    setStickerFiles((prev) =>
      prev.map((s) =>
        s.id === id
          ? {
              ...s,
              [field]: field === 'productKey' ? value.replace(/\s+/g, '_').toUpperCase() : value,
            }
          : s
      )
    );
  };

  const resetUploadedPack = () => {
    setStickerFiles((prev) =>
      prev.map((sticker) => ({
        ...sticker,
        status: 'pending',
        error: undefined,
        assetUrl: undefined,
        savedProductKey: undefined,
      }))
    );
    setPackSaveStatus('idle');
    setPackSaveError('');
  };

  const handleUploadAll = async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    if (!stickerFiles.length) {
      toast.error('Add at least one sticker file.');
      return;
    }

    if (liveQuestOnly) {
      if (!liveQuestRewardId) {
        toast.error('Choose which Live Quest reward this pack belongs to.');
        return;
      }
      if (stickerFiles.length < LIVE_QUEST_PACK_MIN || stickerFiles.length > LIVE_QUEST_PACK_MAX) {
        toast.error(`A Live Quest sticker pack holds ${LIVE_QUEST_PACK_MIN} to ${LIVE_QUEST_PACK_MAX} stickers.`);
        return;
      }
    }

    // A Live Quest pack is a free reward: no price.
    const priceTokens = liveQuestOnly ? 0 : Number(sharedSettings.PriceTokens);
    if (!liveQuestOnly && (!sharedSettings.PriceTokens.trim() || isNaN(priceTokens) || priceTokens < 0)) {
      toast.error('PriceTokens must be a valid non-negative number.');
      return;
    }

    const displayOrderStart = Number(sharedSettings.DisplayOrderStart);
    if (!sharedSettings.DisplayOrderStart.trim() || isNaN(displayOrderStart) || displayOrderStart < 0) {
      toast.error('Display Order Start must be a valid non-negative number.');
      return;
    }

    const pending = stickerFiles.filter((s) => s.status === 'pending' || s.status === 'error');
    const canRetryPack =
      liveQuestOnly &&
      packSaveStatus !== 'saved' &&
      stickerFiles.every((sticker) => sticker.status === 'done' && sticker.assetUrl && sticker.savedProductKey);
    if (!pending.length && !canRetryPack) {
      toast.info('All stickers are already uploaded.');
      return;
    }

    setUploading(true);
    setCompletedCount(0);
    let uploadedThisRun = 0;
    const savedMembers = new Map<string, StickerPackMember>();
    stickerFiles.forEach((sticker) => {
      if (sticker.status === 'done' && sticker.assetUrl && sticker.savedProductKey) {
        savedMembers.set(sticker.id, {
          productKey: sticker.savedProductKey,
          assetUrl: sticker.assetUrl,
          mimeType: sticker.file.type || 'image/png',
        });
      }
    });

    for (let i = 0; i < pending.length; i++) {
      const sticker = pending[i];
      // Live Quest packs get their own keys, so two packs can both have a sticker called "Happy".
      const productKey = liveQuestOnly ? `LQ_${liveQuestRewardId.toUpperCase()}_${sticker.productKey}` : sticker.productKey;

      setStickerFiles((prev) =>
        prev.map((s) => (s.id === sticker.id ? { ...s, status: 'uploading', error: undefined } : s))
      );

      try {
        // Step 1: get presigned upload URL
        const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ProductKey: productKey,
            ContentType: sticker.file.type || 'image/png',
            AssetType: 'thumbnail',
          }),
        });
        const presignedJson = await presignedRes.json();
        if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

        const uploadData = presignedJson.data;

        // Step 2: upload the PNG directly to R2
        const uploadRes = await fetch(uploadData.UploadUrl, {
          method: 'PUT',
          body: await sticker.file.arrayBuffer(),
        });
        if (!uploadRes.ok) throw new Error('Failed to upload image to storage');

        const assetUrl: string = uploadData.AssetUrl;

        // Step 3: a sticker with this ProductKey already exists (e.g. re-uploading a
        // replacement thumbnail for the same sticker) — replace its thumbnail in place
        // rather than creating a duplicate product. updateWalletShopProduct replaces
        // MediaAssets wholesale, so we keep every other asset the product already has.
        const existing = existingStickers.get(productKey);
        if (existing) {
          const existingAssets = existing.MediaAssets || [];
          const nextAssets: WalletShopMediaAsset[] = existingAssets.some((a) => a.Type === 'thumbnail')
            ? existingAssets.map((a) =>
                a.Type === 'thumbnail'
                  ? { Type: 'thumbnail', Url: assetUrl, MimeType: sticker.file.type || 'image/png' }
                  : a
              )
            : [...existingAssets, { Type: 'thumbnail', Url: assetUrl, MimeType: sticker.file.type || 'image/png' }];

          const updateRes = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              ProductKey: productKey,
              Environment: existing.Environment || sharedSettings.Environment,
              MediaAssets: nextAssets,
            }),
          });
          const updateJson = await updateRes.json();
          if (!updateRes.ok) throw new Error(updateJson.message || 'Failed to update existing sticker');
        } else {
          const productRes = await fetch(`${walletApiBase}/createWalletShopProduct`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              ProductKey: productKey,
              Name: sticker.name,
              Description: `${sticker.name} sticker.`,
              CategoryKey: 'STICKER',
              Type: 'sticker',
              CurrencyType: 'token',
              PriceCoins: 0,
              PriceTokens: priceTokens,
              AvailabilityType: sharedSettings.AvailabilityType,
              DisplayOrder: displayOrderStart + i,
              Environment: sharedSettings.Environment,
              // Live Quest packs are inactive on purpose: the Shop only lists active products.
              IsActive: !liveQuestOnly,
              ...(liveQuestOnly
                ? {
                    Tags: ['live_quest', 'sticker_pack', liveQuestRewardId],
                    Metadata: { LiveQuestOnly: true, LiveQuestRewardId: liveQuestRewardId },
                  }
                : {}),
              MediaAssets: [{ Type: 'thumbnail', Url: assetUrl, MimeType: sticker.file.type || 'image/png' }],
            }),
          });
          const productJson = await productRes.json();
          if (!productRes.ok) throw new Error(productJson.message || 'Failed to create product');
        }

        const member = {
          productKey,
          assetUrl,
          mimeType: sticker.file.type || 'image/png',
        };
        savedMembers.set(sticker.id, member);
        uploadedThisRun++;
        setCompletedCount(uploadedThisRun);
        setStickerFiles((prev) =>
          prev.map((s) =>
            s.id === sticker.id
              ? { ...s, status: 'done', assetUrl, savedProductKey: productKey, error: undefined }
              : s
          )
        );
      } catch (err) {
        setStickerFiles((prev) =>
          prev.map((s) =>
            s.id === sticker.id
              ? { ...s, status: 'error', error: (err as Error).message || 'Upload failed' }
              : s
          )
        );
      }
    }

    const failedCount = pending.length - uploadedThisRun;
    if (liveQuestOnly && failedCount === 0 && savedMembers.size === stickerFiles.length) {
      setPackSaveStatus('saving');
      setPackSaveError('');
      try {
        const reward = LIVE_QUEST_PACK_REWARDS.find((item) => item.id === liveQuestRewardId);
        if (!reward) throw new Error('The selected Live Quest sticker-pack reward no longer exists.');
        const members = stickerFiles.map((sticker) => savedMembers.get(sticker.id)).filter((member): member is StickerPackMember => !!member);
        const packResponse = await fetch(`${walletApiBase}/createWalletShopProduct`, {
          method: 'POST',
          headers,
          body: JSON.stringify(buildLiveQuestStickerPackPayload(reward, members, sharedSettings.Environment)),
        });
        const packJson = await packResponse.json();
        if (!packResponse.ok) throw new Error(packJson.message || 'Failed to save the sticker pack');

        const syncResponse = await fetch(`${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live/admin/live-quest-sticker-packs/sync`, {
          method: 'POST', headers, body: JSON.stringify({ rewardId: reward.id }),
        });
        const syncJson = await syncResponse.json();
        if (!syncResponse.ok) throw new Error(syncJson.message || 'Pack saved, but chat stickers did not sync');

        setPackSaveStatus('saved');
        setUploading(false);
        toast.success(`${reward.title} saved with ${members.length} stickers.`);
        navigate(`/live-quest-assets?track=${reward.track}&savedPack=${reward.id}`);
        return;
      } catch (error) {
        const message = (error as Error).message || 'Failed to save the sticker pack';
        setPackSaveStatus('error');
        setPackSaveError(message);
        toast.error(`The stickers uploaded, but the pack was not saved: ${message}`);
      }
    } else if (uploadedThisRun > 0 && failedCount === 0) {
      toast.success(`All ${uploadedThisRun} sticker(s) uploaded successfully!`);
    } else if (uploadedThisRun > 0) {
      toast.warn(`${uploadedThisRun} uploaded, ${failedCount} failed.`);
    } else {
      toast.error('All uploads failed.');
    }
    setUploading(false);
  };

  const pendingCount = stickerFiles.filter((s) => s.status === 'pending' || s.status === 'error').length;
  const doneCount = stickerFiles.filter((s) => s.status === 'done').length;
  const canSavePack =
    liveQuestOnly &&
    packSaveStatus !== 'saved' &&
    stickerFiles.length > 0 &&
    doneCount === stickerFiles.length &&
    stickerFiles.every((sticker) => sticker.assetUrl && sticker.savedProductKey);

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Bulk Sticker Upload</h1>
            <p className='text-sm text-gray-400'>
              Set shared settings once, select up to {MAX_FILES} PNG sticker files, then upload all.
            </p>
          </div>
          <button
            onClick={() => navigate(liveQuestOnly ? '/live-quest-assets' : '/products?category=GIFT')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20'
          >
            {liveQuestOnly ? 'Back to Live Quest Assets' : 'Back to Products'}
          </button>
        </div>

        {/* Live Quest only */}
        <div className='bg-fuchsia-500/10 border border-fuchsia-400/20 rounded-2xl p-6'>
          <label className='flex items-start gap-3 text-sm text-white'>
            <input
              type='checkbox'
              checked={liveQuestOnly}
              onChange={(e) => {
                setLiveQuestOnly(e.target.checked);
                resetUploadedPack();
              }}
              className='mt-1'
            />
            <span>
              <span className='font-semibold'>Live Quest reward only</span>
              <span className='block text-xs text-gray-300'>
                The pack is a free reward for completing a level. It has no price and is not listed in the Shop or
                Wallet Assets. A pack holds {LIVE_QUEST_PACK_MIN} to {LIVE_QUEST_PACK_MAX} stickers (PNG or WebP).
              </span>
            </span>
          </label>
          {liveQuestOnly && (
            <div className='mt-4 max-w-md'>
              <label className='block text-xs text-gray-300 mb-1'>Reward this pack belongs to *</label>
              <select
                value={liveQuestRewardId}
                onChange={(e) => {
                  setLiveQuestRewardId(e.target.value);
                  resetUploadedPack();
                }}
                className='w-full px-3 py-2 bg-slate-900 border border-white/20 rounded-lg text-white'
              >
                <option value=''>Choose a reward</option>
                {LIVE_QUEST_PACK_REWARDS.map((reward) => (
                  <option key={reward.id} value={reward.id}>
                    {reward.track === 'plus' ? 'Plus' : 'Free'} LV {reward.level}: {reward.title}
                  </option>
                ))}
              </select>
              <p className='mt-2 text-xs text-gray-400'>
                {stickerFiles.length} sticker{stickerFiles.length === 1 ? '' : 's'} selected.
              </p>
            </div>
          )}
        </div>

        {/* Shared Settings */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6'>
          <h2 className='text-lg font-semibold text-white mb-4'>Shared Settings</h2>
          <p className='text-xs text-gray-400 mb-4'>
            These values apply to every sticker in this batch. Only the name and product key differ per sticker (derived from file names).
          </p>
          <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
            {!liveQuestOnly && (
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Price (Tokens) *</label>
              <input
                type='number'
                min='0'
                value={sharedSettings.PriceTokens}
                onChange={(e) => setSharedSettings((prev) => ({ ...prev, PriceTokens: e.target.value }))}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
              />
            </div>
            )}
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Display Order Start</label>
              <input
                type='number'
                min='0'
                value={sharedSettings.DisplayOrderStart}
                onChange={(e) => setSharedSettings((prev) => ({ ...prev, DisplayOrderStart: e.target.value }))}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
              />
              <p className='text-[11px] text-gray-500 mt-1'>Auto-incremented per sticker.</p>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Availability</label>
              <select
                value={sharedSettings.AvailabilityType}
                onChange={(e) =>
                  setSharedSettings((prev) => ({
                    ...prev,
                    AvailabilityType: e.target.value as 'unlimited' | 'limited',
                  }))
                }
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
              >
                <option value='unlimited'>Unlimited</option>
                <option value='limited'>Limited</option>
              </select>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Environment</label>
              <select
                value={sharedSettings.Environment}
                onChange={(e) =>
                  setSharedSettings((prev) => ({
                    ...prev,
                    Environment: e.target.value as 'dev' | 'prod',
                  }))
                }
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
              >
                <option value='prod'>prod</option>
                <option value='dev'>dev</option>
              </select>
            </div>
          </div>
        </div>

        {/* File Picker */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6'>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <h2 className='text-lg font-semibold text-white'>Sticker Files</h2>
              <p className='text-xs text-gray-400 mt-0.5'>
                {stickerFiles.length} / {MAX_FILES} files added
                {doneCount > 0 && ` · ${doneCount} uploaded`}
              </p>
            </div>
            <label className='cursor-pointer px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'>
              + Add PNG Files
              <input
                type='file'
                accept='image/png,image/jpeg,image/webp'
                multiple
                className='hidden'
                onChange={handleFilesSelected}
                disabled={uploading || stickerFiles.length >= MAX_FILES}
              />
            </label>
          </div>

          {stickerFiles.length === 0 ? (
            <div className='border-2 border-dashed border-white/10 rounded-xl py-16 flex flex-col items-center justify-center gap-3'>
              <p className='text-sm text-gray-400'>No files selected yet.</p>
              <label className='cursor-pointer px-5 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'>
                Select PNG Stickers
                <input
                  type='file'
                  accept='image/png,image/jpeg,image/webp'
                  multiple
                  className='hidden'
                  onChange={handleFilesSelected}
                  disabled={uploading}
                />
              </label>
              <p className='text-xs text-gray-500'>Up to {MAX_FILES} files · PNG, JPEG, or WEBP</p>
            </div>
          ) : (
            <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'>
              {stickerFiles.map((sticker) => (
                <div
                  key={sticker.id}
                  className={`relative bg-white/5 border rounded-xl overflow-hidden flex flex-col ${
                    sticker.status === 'done'
                      ? 'border-emerald-400/40'
                      : sticker.status === 'error'
                      ? 'border-red-400/40'
                      : sticker.status === 'uploading'
                      ? 'border-amber-400/40'
                      : 'border-white/10'
                  }`}
                >
                  {/* Status badge */}
                  <div className='absolute top-2 left-2 z-10 flex flex-col gap-1 items-start'>
                    {sticker.status === 'done' && (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>Done</span>
                    )}
                    {sticker.status === 'uploading' && (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>Uploading…</span>
                    )}
                    {sticker.status === 'error' && (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>Error</span>
                    )}
                    {sticker.status === 'pending' && (
                      existingStickers.has(sticker.productKey) ? (
                        <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-sky-500/80 text-white'>Will update existing</span>
                      ) : (
                        <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/20 text-white'>New sticker</span>
                      )
                    )}
                  </div>

                  {/* Remove button */}
                  {sticker.status !== 'uploading' && (
                    <button
                      onClick={() => removeSticker(sticker.id)}
                      className='absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-black/50 text-gray-300 hover:text-white text-xs flex items-center justify-center'
                    >
                      ✕
                    </button>
                  )}

                  {/* Preview */}
                  <div className='h-28 bg-white/10 flex items-center justify-center overflow-hidden'>
                    <img
                      src={sticker.previewUrl}
                      alt={sticker.name}
                      className='w-full h-full object-contain p-1'
                    />
                  </div>

                  {/* Editable fields */}
                  <div className='p-2 space-y-1.5 flex-1'>
                    <input
                      value={sticker.name}
                      onChange={(e) => updateField(sticker.id, 'name', e.target.value)}
                      disabled={sticker.status === 'uploading' || sticker.status === 'done'}
                      className='w-full px-2 py-1 text-xs bg-white/10 border border-white/10 rounded text-white placeholder-gray-500 disabled:opacity-50'
                      placeholder='Name'
                    />
                    <input
                      value={sticker.productKey}
                      onChange={(e) => updateField(sticker.id, 'productKey', e.target.value)}
                      disabled={sticker.status === 'uploading' || sticker.status === 'done'}
                      className='w-full px-2 py-1 text-[10px] bg-white/10 border border-white/10 rounded text-gray-300 placeholder-gray-500 disabled:opacity-50 font-mono'
                      placeholder='PRODUCT_KEY'
                    />
                    {sticker.error && (
                      <p className='text-[10px] text-red-300 leading-tight'>{sticker.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upload Button */}
        {stickerFiles.length > 0 && (
          <div className='flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-6 py-4'>
            <div className='text-sm text-gray-300'>
              {uploading
                ? packSaveStatus === 'saving'
                  ? 'Saving the sticker pack…'
                  : `Uploading… ${completedCount} / ${pendingCount + completedCount} done`
                : packSaveStatus === 'error'
                  ? `Pack save failed: ${packSaveError}`
                  : canSavePack
                    ? 'All stickers uploaded. The pack is ready to save.'
                    : `${pendingCount} sticker(s) ready to upload`}
            </div>
            <button
              onClick={handleUploadAll}
              disabled={uploading || (pendingCount === 0 && !canSavePack)}
              className='px-6 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-50'
            >
              {uploading
                ? packSaveStatus === 'saving'
                  ? 'Saving Pack…'
                  : 'Uploading…'
                : canSavePack
                  ? 'Save Sticker Pack'
                  : `Upload ${pendingCount} Sticker${pendingCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkStickerUpload;
